import React, { useEffect, useMemo, useState } from "react";
import { Btn, Field, TextArea } from "./primitives";
import { formatQuantity } from "../lib/providers";
import { X, ArrowsClockwise } from "@phosphor-icons/react";

/**
 * Credit-note creation modal. Lets the user choose between:
 *   - a full credit note for the entire invoice, or
 *   - a partial credit note for selected line indexes (with optional quantity override).
 *
 * onSubmit({ mode: "full" | "partial", line_indexes, quantities, reason }) -> Promise
 */
export default function CreditNoteModal({ open, onClose, invoice, onSubmit, lang = "de" }) {
  const lines = invoice?.lines || [];
  const [mode, setMode] = useState("full");
  const [selected, setSelected] = useState({});  // index -> bool
  const [quantities, setQuantities] = useState({});  // index -> quantity override
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setMode("full");
      setSelected({});
      setQuantities({});
      setReason("");
    }
  }, [open]);

  const totalSelected = useMemo(() => {
    if (mode === "full") return lines.reduce((s, l) => s + Number(l.line_total || 0), 0);
    return lines.reduce((s, l, i) => {
      if (!selected[i]) return s;
      const q = quantities[i] !== undefined && quantities[i] !== "" ? Number(quantities[i]) : Number(l.quantity || 0);
      const unit = Number(l.unit_price || 0);
      const disc = Number(l.discount_percent || 0);
      return s + q * unit * (1 - disc / 100);
    }, 0);
  }, [mode, selected, quantities, lines]);

  if (!open) return null;

  const allSelected = lines.length > 0 && lines.every((_, i) => selected[i]);
  const toggleAll = () => {
    if (allSelected) setSelected({});
    else setSelected(Object.fromEntries(lines.map((_, i) => [i, true])));
  };

  const submit = async () => {
    if (mode === "partial") {
      const idxs = Object.entries(selected).filter(([, v]) => v).map(([k]) => Number(k));
      if (idxs.length === 0) return;
      const quantsClean = {};
      idxs.forEach((i) => {
        if (quantities[i] !== undefined && quantities[i] !== "" && !Number.isNaN(Number(quantities[i]))) {
          quantsClean[String(i)] = Number(quantities[i]);
        }
      });
      setBusy(true);
      try {
        await onSubmit({ mode: "partial", line_indexes: idxs, quantities: quantsClean, reason });
      } finally {
        setBusy(false);
      }
    } else {
      setBusy(true);
      try {
        await onSubmit({ mode: "full", reason });
      } finally {
        setBusy(false);
      }
    }
  };

  const fmt = (n) => new Intl.NumberFormat(lang === "de" ? "de-DE" : "en-US", { style: "currency", currency: "EUR" }).format(Number(n || 0));

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="credit-note-modal-overlay">
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-sm w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="credit-note-modal">
        <div className="px-6 py-4 border-b border-[var(--border)] flex items-center justify-between">
          <div>
            <div className="label-overline">{lang === "de" ? "Gutschrift erstellen" : "Create credit note"}</div>
            <h2 className="font-heading text-xl font-semibold">{invoice?.number || ""}{invoice?.title ? ` – ${invoice.title}` : ""}</h2>
          </div>
          <button type="button" onClick={onClose} className="text-[var(--muted)] hover:text-[var(--ink)]" data-testid="credit-note-close">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setMode("full")}
              data-testid="credit-mode-full"
              className={`flex-1 px-4 py-3 rounded-sm border text-sm transition-colors ${
                mode === "full"
                  ? "bg-[var(--brand)] text-white border-[var(--brand)]"
                  : "bg-[var(--surface)] border-[var(--border)] text-[var(--muted)] hover:bg-[var(--row-hover)]"
              }`}
            >
              <div className="font-medium">{lang === "de" ? "Komplette Rechnung" : "Full invoice"}</div>
              <div className={`text-xs mt-1 ${mode === "full" ? "text-white/80" : "text-[var(--muted-2)]"}`}>
                {lang === "de" ? "Alle Positionen mit voller Menge gutschreiben" : "Credit all lines at full quantity"}
              </div>
            </button>
            <button
              type="button"
              onClick={() => setMode("partial")}
              data-testid="credit-mode-partial"
              className={`flex-1 px-4 py-3 rounded-sm border text-sm transition-colors ${
                mode === "partial"
                  ? "bg-[var(--brand)] text-white border-[var(--brand)]"
                  : "bg-[var(--surface)] border-[var(--border)] text-[var(--muted)] hover:bg-[var(--row-hover)]"
              }`}
            >
              <div className="font-medium">{lang === "de" ? "Einzelne Positionen" : "Selected lines"}</div>
              <div className={`text-xs mt-1 ${mode === "partial" ? "text-white/80" : "text-[var(--muted-2)]"}`}>
                {lang === "de" ? "Positionen wählen + optional Menge anpassen" : "Pick lines + optional quantity"}
              </div>
            </button>
          </div>

          {mode === "partial" && (
            <div className="border border-[var(--border)] rounded-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[var(--bg)] text-[var(--muted)]">
                  <tr>
                    <th className="w-10 px-3 py-2 text-left">
                      <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="credit-select-all" />
                    </th>
                    <th className="text-left px-3 py-2 label-overline">{lang === "de" ? "Position" : "Line"}</th>
                    <th className="text-right px-3 py-2 label-overline w-28">{lang === "de" ? "Menge orig." : "Orig. qty"}</th>
                    <th className="text-right px-3 py-2 label-overline w-28">{lang === "de" ? "Menge GS" : "Credit qty"}</th>
                    <th className="text-right px-3 py-2 label-overline w-28">{lang === "de" ? "Wert" : "Amount"}</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-3 py-6 text-center text-[var(--muted-2)]">
                        {lang === "de" ? "Keine Positionen." : "No lines."}
                      </td>
                    </tr>
                  )}
                  {lines.map((l, i) => {
                    const isSel = !!selected[i];
                    const q = quantities[i] !== undefined && quantities[i] !== "" ? Number(quantities[i]) : Number(l.quantity || 0);
                    const amt = q * Number(l.unit_price || 0) * (1 - Number(l.discount_percent || 0) / 100);
                    return (
                      <tr key={i} className={`border-t border-[var(--border)] ${isSel ? "bg-[var(--brand-soft)]" : ""}`}>
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={isSel}
                            onChange={(e) => setSelected({ ...selected, [i]: e.target.checked })}
                            data-testid={`credit-select-line-${i}`}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <div className="font-medium text-[var(--ink)]">{l.name || "—"}</div>
                          {l.description && <div className="text-xs text-[var(--muted-2)] line-clamp-1">{l.description}</div>}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-[var(--muted)]">
                          {formatQuantity(l.quantity, l.unit)} {l.unit || ""}
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            step={(l.unit || "").toLowerCase() === "stk" ? "1" : "0.01"}
                            min="0"
                            disabled={!isSel}
                            value={quantities[i] !== undefined ? quantities[i] : (isSel ? l.quantity : "")}
                            onChange={(e) => setQuantities({ ...quantities, [i]: e.target.value })}
                            placeholder={formatQuantity(l.quantity, l.unit)}
                            className="w-full h-8 px-2 text-right bg-[var(--surface)] border border-[var(--border)] rounded-sm text-sm focus:border-[var(--brand)] focus:outline-none disabled:opacity-40"
                            data-testid={`credit-qty-${i}`}
                          />
                        </td>
                        <td className="px-3 py-2 text-right font-medium">
                          {isSel ? fmt(amt) : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <Field label={lang === "de" ? "Grund (optional)" : "Reason (optional)"}>
            <TextArea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={lang === "de" ? "z. B. Defekte Ware, Mengenkorrektur, …" : "e.g. damaged goods, qty correction, …"}
              data-testid="credit-reason"
            />
          </Field>

          <div className="flex items-center justify-between bg-[var(--bg)] border border-[var(--border)] rounded-sm px-4 py-3">
            <span className="text-sm text-[var(--muted)]">
              {lang === "de" ? "Gutschriftbetrag (netto)" : "Credit amount (net)"}
            </span>
            <span className="font-heading text-xl font-semibold text-[var(--brand)]" data-testid="credit-total">
              {fmt(-Math.abs(totalSelected))}
            </span>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-[var(--border)] flex justify-end gap-2">
          <Btn variant="ghost" onClick={onClose} data-testid="credit-cancel">{lang === "de" ? "Abbrechen" : "Cancel"}</Btn>
          <Btn
            onClick={submit}
            disabled={busy || (mode === "partial" && Object.values(selected).every((v) => !v))}
            data-testid="credit-submit"
          >
            <ArrowsClockwise size={16} /> {busy ? "…" : (lang === "de" ? "Gutschrift erstellen" : "Create credit note")}
          </Btn>
        </div>
      </div>
    </div>
  );
}
